<template>
  <div class="card">
    <DataTable
      lazy
      :value="tableData"
      :paginator="true"
      :rows="rows"
      removableSort
      :loading="isLoading"
      :totalRecords="totalRecords"
      :currentPage="currentPage"
      paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
      :rowsPerPageOptions="[10, 25, 100]"
      currentPageReportTemplate="Affichage de {first} à {last} sur {totalRecords} entrées"
      @page="onPaginate($event)"
      @sort="onSort($event)"
    >
      <template #header>
        <div class="flex flex-row flex-nowrap card-header border-0">
          <div class="flex card-toolbar w-full">
            <div class="w-full">
              <span class="p-input-icon-left w-10/12">
                  <i class="pi pi-search" />
                  <InputText type="text" class="w-9/12" v-model="criteria" placeholder="Rechercher..." @keyup.enter="onSearchInputDt()" />
              </span>
            </div>
          </div>
          <div class="card-toolbar w-full">
            <div class="flex flex-col justify-center">
              <p>Nombre total de moyens d'expédition : {{ totalRecords }}</p>
            </div>
          </div>
          <div class="card-toolbar w-full">
            <div class="flex justify-end">
              <Button size="small" class="btn btn-primary p-2 ps-2 pe-2"
                      style="margin-right: 0.5em" 
                      @click="router.push({ name: 'addShippingMethod', params: { uuid: supplierUuid.value } })" 
                      title="Ajouter un moyen d'expédition">Ajouter un moyen d'expédition
              </Button>
            </div>
          </div>
        </div>
      </template>

      <div class="card-body pt-0">
        <Column field="name" header="Intitulé" sortable>
          <template #body="slotProps">
            <span> {{ slotProps.data.name }} </span>
          </template>
        </Column>
        <Column field="transporter" header="transporteur" sortable>
          <template #body="slotProps">
            <span> {{ slotProps.data.transporter.name }} </span>
          </template>
        </Column>
        <Column field="deliveryTime" header="Délai" sortable>
          <template #body="slotProps">
            <span> {{ slotProps.data.deliveryTime }} </span>
          </template>
        </Column>
        <Column field="createdBy" header="Créé par" sortable>
          <template #body="slotProps">
            <span> {{ slotProps.data.createdBy }} </span>
          </template>
        </Column>
        <Column field="status" header="Etat" sortable>
          <template #body="slotProps">
            <span v-if="slotProps.data.status" class="badge badge-success">
              Actif
            </span>
            <span v-else class="badge badge-warning"> Inactif </span>
          </template>
        </Column>
        <Column field="action" header="Action">
          <template #body="slotProps">
            <div class="d-flex flex-row">
              <Button size="small" icon="pi pi-pencil" class="p-edition"
                      style="margin-right: 0.5em" title="Éditer"
                      @click="router.push({ name: 'editShippingMethod', params: { uuid: slotProps.data.uuid } })">
              </Button>
              <Button size="small" icon="pi pi-trash" class="p-button-danger"
                      style="margin-right: 0.5em" title="Éditer" @click="onDelete(slotProps.data)">
              </Button>
              <InputSwitch
                v-model="slotProps.data.status"
                @input="changeState(slotProps.data.uuid, slotProps.data.status)"
              />
            </div>
          </template>
        </Column>
      </div>
      <template #empty>Aucun moyen d'expédition ne correspond à votre recherche</template>
    </DataTable>
  </div>
</template>
<script setup lang="ts">
import DataTable from "primevue/datatable";
import { ref, onMounted } from 'vue';
import { useRoute } from "vue-router";
import InputText from "primevue/inputtext";
import Button from "primevue/button";
import Column from "primevue/column";
import InputSwitch from "primevue/inputswitch";
import swal from "@/_helpers/swal";
import router from "@/router";
import {
  deleteShippingMethod,
  fetchShippingMethodBySupplier,
  IShippingMethod,
  triggerShippingMethodState
} from "@/_services/shippingMethod/shippingMethod"
const shippingMethod = Array<IShippingMethod>();
const tableData = ref<Array<IShippingMethod>>(shippingMethod);
const rows = ref<number>(10);
const isLoading = ref<boolean>(true);
const totalRecords = ref<number>(0);
const currentPage = ref<number>(0);
const criteria = ref<string>("");
const page = ref<number>(1);
const modeSortable = ref<boolean>(false);
const defaultsSort = {
  fieldLabel: "order[createdAt]",
  fieldValue: "desc",
};
const supplierUuid = ref<string>("");
const route = useRoute();

onMounted(async () => {
  supplierUuid.value = route.params.uuid as string;
  await populateDatatable(rows.value, page.value, criteria.value, defaultsSort);
});

const populateDatatable = async (itemsPerPage, page, criteria?, sort?) => {
  showSpinner();
  const [loadingDt, totalRecordsDt, tableDataDt] = await fetchShippingMethodBySupplier(
    supplierUuid.value,
    itemsPerPage,
    page,
    criteria,
    sort
  );
  totalRecords.value = totalRecordsDt.value;
  tableData.value = tableDataDt.value;
  isLoading.value = loadingDt.value;
};

async function onPaginate(e) {
  rows.value = e.rows;
  populateDatatable(e.rows, e.page + 1, criteria.value, defaultsSort);
}

const onDelete = (data: IShippingMethod) => {
    swal
        .confirm("Êtes vous sur de vouloir supprimer le moyen d’expédition ?")
        .then(async (result) => {
            if (result.isConfirmed) {
               const deletionAllowed = await deleteShippingMethod(data.uuid);
               if(!deletionAllowed) {
                   swal.error("Vous ne pouvez pas supprimer le moyen d’expédition car il est rattaché à au moins 1 produit actif.");
               } else {
                   swal.success("Supprimer !", "Vous venez de supprimer un moyen d’expédition");
                   populateDatatable(rows.value, page.value, criteria.value);
               }
            }
        });
};

const onSort = (e) => {
  if (e.sortField) {
    const sort = {
      fieldLabel: "order[" + e.sortField + "]",
      fieldValue: e.sortOrder === 1 ? "asc" : "desc",
    };
    modeSortable.value = true;
    populateDatatable(rows.value, page.value, criteria.value, sort);
  } else {
    modeSortable.value = false;
    populateDatatable(rows.value, page.value, criteria.value, defaultsSort);
  }
};

const changeState = async (uuid: string, status: number) => {
  const confirmationMessage = status
    ? "Êtes-vous sûr de vouloir activer le moyen d’expédition ?"
    : "Êtes-vous sûr de vouloir désactiver le moyen d’expédition ?";

  const result = await swal.confirm(confirmationMessage);

  if (result.isConfirmed) {
    await triggerShippingMethodState(uuid, status);
    const successMessage = status
      ? "Vous venez d'activer un moyen d’expédition."
      : "Vous venez de désactiver un moyen d’expédition.";

    swal.info(successMessage);
  }
  populateDatatable(rows.value, page.value, criteria.value, defaultsSort);
};

const onSearchInputDt = () => {
  if (0 === criteria.value.length || 3 <= criteria.value.length) {
    populateDatatable(
      rows.value,
      page.value,
    "" != criteria.value ? criteria.value : ""
    );
  }
}

const showSpinner = () => {
    isLoading.value = true;
}

const hideSpinner = () => {
    isLoading.value = false;
}
</script>

